import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CheckCheck, Plus, Save } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Selo } from '../../componentes/base';
import { Carregando, Erro, Vazio } from '../../componentes/estados';
import { BuscaRemota, Entrada, Rotulo, Selecao } from '../../componentes/formulario';
import { Confirmar, Drawer, Modal } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { get, mensagemDeErro, post } from '../../nucleo/api';
import { dataHora, nomeCadastro } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { invalidarEstoque, nomeInventario, qtd, rotuloProduto, useDepositos } from './comum';
import type { Inventario as TInventario, InventarioDetalhe, ItemInventario } from './tipos';
import { TextoCortado } from '../../componentes/texto-cortado';

/** M9.5: abrir, contar e fechar inventários. O fechamento gera os ajustes das diferenças. */
export function Inventario() {
  const { t } = useTranslation('estoque');
  const s = useSessao();
  const [abrir, setAbrir] = useState(false);
  const [aberto, setAberto] = useState<TInventario | null>(null);

  const colunas: Coluna<TInventario>[] = [
    { chave: 'nome', rotulo: t('campos.nome'), principal: true, render: (i) => <span className="font-medium">{nomeInventario(i.nome)}</span> },
    { chave: 'deposito_nome', rotulo: t('campos.deposito') },
    { chave: 'status', rotulo: t('campos.situacao'), render: (i) => <Selo cor={i.status === 'aberto' ? 'azul' : 'verde'}>{t(`inventario.status.${i.status}`)}</Selo> },
    { chave: 'itens_contados', rotulo: t('inventario.contados'), alinhar: 'direita', mono: true, render: (i) => `${i.itens_contados}/${i.itens_total}` },
    { chave: 'itens_com_diferenca', rotulo: t('inventario.comDiferenca'), alinhar: 'direita', mono: true, render: (i) => (i.status === 'fechado' ? i.itens_com_diferenca : '') },
    { chave: 'aberto_em', rotulo: t('inventario.abertoEm'), mono: true, render: (i) => dataHora(i.aberto_em) },
    { chave: 'fechado_em', rotulo: t('inventario.fechadoEm'), mono: true, render: (i) => (i.fechado_em ? dataHora(i.fechado_em) : '') },
  ];

  return (
    <>
      {s.pode('estoque.inventariar') && (
        <AcaoPrimaria>
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setAbrir(true)}>
            {t('acoes.abrirInventario')}
          </Botao>
        </AcaoPrimaria>
      )}
      <Tabela<TInventario> tela="estoque.inventarios" caminho="/estoque/inventarios" colunas={colunas} aoClicar={(i) => setAberto(i)} vazio={<Vazio texto={t('vazio.inventarios')} />} />
      <ModalAbrir aberto={abrir} aoFechar={() => setAbrir(false)} aoAbrir={(i) => setAberto(i)} />
      <DrawerInventario inventario={aberto} aoFechar={() => setAberto(null)} />
    </>
  );
}

function ModalAbrir({ aberto, aoFechar, aoAbrir }: { aberto: boolean; aoFechar: () => void; aoAbrir: (i: TInventario) => void }) {
  const { t } = useTranslation('estoque');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const depositos = useDepositos(aberto);
  const [deposito, setDeposito] = useState('');
  const [nome, setNome] = useState('');
  const efetivo = deposito || depositos.data?.itens.find((d) => d.padrao)?.id || '';
  const criar = useMutation({
    mutationFn: () => post<{ id: string }>('/estoque/inventarios', { deposito_id: efetivo, nome: nome.trim() || undefined }),
    onSuccess: async (r) => {
      await invalidarEstoque(qc);
      const novo = await get<InventarioDetalhe>(`/estoque/inventarios/${r.id}`);
      setNome('');
      aoFechar();
      aoAbrir(novo);
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={t('acoes.abrirInventario')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" disabled={!efetivo} carregando={criar.isPending} onClick={() => criar.mutate()}>
            {t('acoes.abrirInventario')}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-tinta-3">{t('inventario.explicacaoAbrir')}</p>
        <Rotulo id="inv-deposito" rotulo={t('campos.deposito')} obrigatorio>
          <Selecao id="inv-deposito" opcoes={(depositos.data?.itens ?? []).filter((d) => d.ativo).map((d) => ({ valor: d.id, rotulo: d.nome }))} value={efetivo} onChange={(e) => setDeposito(e.target.value)} />
        </Rotulo>
        <Rotulo id="inv-nome" rotulo={t('campos.nome')} ajuda={t('inventario.nomePadrao')}>
          <Entrada id="inv-nome" value={nome} onChange={(e) => setNome(e.target.value)} maxLength={120} />
        </Rotulo>
      </div>
    </Modal>
  );
}

function DrawerInventario({ inventario, aoFechar }: { inventario: TInventario | null; aoFechar: () => void }) {
  const { t } = useTranslation('estoque');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [contagens, setContagens] = useState<Record<string, string>>({});
  const [fechar, setFechar] = useState(false);
  const [extra, setExtra] = useState<{ id: string; rotulo: string } | null>(null);
  const [extraQtd, setExtraQtd] = useState('');
  const q = useQuery({ queryKey: ['/estoque/inventarios', 'detalhe', inventario?.id], queryFn: () => get<InventarioDetalhe>(`/estoque/inventarios/${inventario!.id}`), enabled: !!inventario });
  const d = q.data;
  const editavel = d?.status === 'aberto' && s.pode('estoque.inventariar');

  const numeroDe = (txt: string) => (txt.trim() === '' ? null : Number(txt.replace(',', '.')));
  const pendentes = (d?.itens ?? []).flatMap((i) => {
    const txt = contagens[i.id];
    if (txt === undefined) return [];
    const n = numeroDe(txt);
    return n === null || Number.isNaN(n) || n < 0 || n === i.quantidade_contada ? [] : [{ i, n }];
  });

  const salvar = useMutation({
    mutationFn: (lista: { produto_id: string; variacao_id: string | null; lote_id: string | null; quantidade: number }[]) => post(`/estoque/inventarios/${inventario!.id}/contagens`, { contagens: lista }),
    onSuccess: async () => {
      setContagens({});
      setExtra(null);
      setExtraQtd('');
      await invalidarEstoque(qc);
      toast.sucesso(tn('comum.salvo'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const encerrar = useMutation({
    mutationFn: () => post(`/estoque/inventarios/${inventario!.id}/fechar`),
    onSuccess: async () => {
      setFechar(false);
      await invalidarEstoque(qc);
      toast.sucesso(t('inventario.fechado'));
    },
    onError: (e) => {
      setFechar(false);
      toast.erro(mensagemDeErro(e));
    },
  });

  const rotuloItem = (i: ItemInventario) => [i.variacao_sku, i.lote_codigo && `${t('campos.lote')} ${i.lote_codigo}`].filter(Boolean).join(', ');

  return (
    <Drawer
      aberto={!!inventario}
      aoFechar={() => {
        setContagens({});
        aoFechar();
      }}
      largura="lg"
      titulo={nomeInventario(d ? d.nome : (inventario?.nome ?? ''))}
      rodape={
        editavel ? (
          <>
            <Botao icone={<Save className="size-4" />} disabled={!pendentes.length} carregando={salvar.isPending} onClick={() => salvar.mutate(pendentes.map(({ i, n }) => ({ produto_id: i.produto_id, variacao_id: i.variacao_id, lote_id: i.lote_id, quantidade: n })))}>
              {t('inventario.salvarContagens', { n: pendentes.length })}
            </Botao>
            <Botao variante="primario" icone={<CheckCheck className="size-4" />} disabled={!d || (d.itens_contados === 0 && pendentes.length === 0) || pendentes.length > 0} onClick={() => setFechar(true)}>
              {t('acoes.fecharInventario')}
            </Botao>
          </>
        ) : (
          <Botao
            variante="fantasma"
            onClick={() => {
              setContagens({});
              aoFechar();
            }}
          >
            {tn('comum.fechar')}
          </Botao>
        )
      }
    >
      {q.isPending ? (
        <Carregando />
      ) : q.isError ? (
        <Erro erro={q.error} aoTentar={() => q.refetch()} />
      ) : (
        d && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-3 text-sm text-tinta-2">
              <Selo cor={d.status === 'aberto' ? 'azul' : 'verde'}>{t(`inventario.status.${d.status}`)}</Selo>
              <span>{d.deposito_nome}</span>
              <span className="numero text-tinta-3">{`${d.itens_contados}/${d.itens_total} ${t('inventario.contados').toLowerCase()}`}</span>
            </div>
            {editavel && <p className="text-sm text-tinta-3">{t('inventario.explicacaoContar')}</p>}

            {d.itens.length === 0 ? (
              <Vazio texto={t('inventario.semItens')} />
            ) : (
              <div className="overflow-x-auto rounded-xl border border-borda">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-borda text-left text-tinta-3">
                      <th scope="col" className="px-3 py-2 font-medium">{t('campos.produto')}</th>
                      <th scope="col" className="px-3 py-2 text-right font-medium">{t('inventario.saldoSistema')}</th>
                      <th scope="col" className="px-3 py-2 text-right font-medium">{t('inventario.contagem')}</th>
                      <th scope="col" className="px-3 py-2 text-right font-medium">{t('inventario.diferenca')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.itens.map((i) => {
                      const txt = contagens[i.id];
                      const valor = txt ?? (i.quantidade_contada === null ? '' : String(i.quantidade_contada));
                      const n = numeroDe(valor);
                      const previa = d.status === 'fechado' ? i.diferenca : n === null || Number.isNaN(n) ? null : Math.round((n - i.saldo_sistema) * 1e4) / 1e4;
                      return (
                        <tr key={i.id} className="border-b border-borda last:border-0">
                          <td className="min-w-0 px-3 py-2">
                            <span className="block truncate font-medium" title={i.produto_nome ?? undefined}>{nomeCadastro(i.produto_nome)}</span>
                            <TextoCortado className="numero block text-xs text-tinta-3">{[i.produto_codigo, rotuloItem(i)].filter(Boolean).join(', ')}</TextoCortado>
                          </td>
                          <td className="numero px-3 py-2 text-right">{`${qtd(i.saldo_sistema)} ${i.unidade ?? ''}`}</td>
                          <td className="px-3 py-2 text-right">
                            {editavel ? (
                              <Entrada
                                aria-label={`${t('inventario.contagem')} ${nomeCadastro(i.produto_nome)}`}
                                inputMode="decimal"
                                className="numero ml-auto h-9 w-28 text-right"
                                value={valor}
                                onChange={(e) => setContagens((x) => ({ ...x, [i.id]: e.target.value }))}
                              />
                            ) : (
                              <span className="numero">{i.quantidade_contada === null ? '' : qtd(i.quantidade_contada)}</span>
                            )}
                          </td>
                          <td className={`numero px-3 py-2 text-right ${previa ? (previa > 0 ? 'text-verde' : 'text-laranja') : 'text-tinta-3'}`}>{previa === null ? '' : `${previa > 0 ? '+' : ''}${qtd(previa)}`}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {editavel && (
              <div className="rounded-xl border border-borda p-3">
                <p className="mb-2 text-sm font-medium">{t('inventario.itemFora')}</p>
                <div className="flex flex-wrap items-end gap-2">
                  <div className="min-w-0 flex-1 basis-60">
                    <BuscaRemota id="inv-extra" def={{ caminho: '/estoque/produtos', params: { ativo: true }, rotulo: rotuloProduto }} valor={extra?.id ?? null} aoMudar={(v, item) => setExtra(v && item ? { id: v, rotulo: rotuloProduto(item) } : null)} />
                  </div>
                  <Entrada aria-label={t('inventario.contagem')} placeholder={t('inventario.contagem')} inputMode="decimal" className="numero w-28 text-right" value={extraQtd} onChange={(e) => setExtraQtd(e.target.value)} />
                  <Botao
                    icone={<Plus className="size-4" />}
                    disabled={!extra || numeroDe(extraQtd) === null || Number.isNaN(numeroDe(extraQtd))}
                    carregando={salvar.isPending}
                    onClick={() => salvar.mutate([{ produto_id: extra!.id, variacao_id: null, lote_id: null, quantidade: numeroDe(extraQtd)! }])}
                  >
                    {t('acoes.adicionar')}
                  </Botao>
                </div>
              </div>
            )}
          </div>
        )
      )}
      <Confirmar aberto={fechar} aoFechar={() => setFechar(false)} aoConfirmar={() => encerrar.mutate()} carregando={encerrar.isPending} titulo={t('confirmar.fecharInventario')} texto={t('confirmar.fecharInventarioTexto')} />
    </Drawer>
  );
}
